"use client";

import { useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import { EMPTY_SELECTED, FILTER_CONFIG } from "./filterConfig";
import { FilterSection } from "./FilterOptions";
import { useContractorFiltersStore } from "@/store/store";
import { inhomzEvents } from "../../../_lib/events";
import { useInhomzTrack } from "../../../_hooks/useInhomzTrack";
import { buildFilterEventProps } from "../_utils/buildFilterEventProps";

// Desktop-only persistent filter column — mobile/tablet keep using FilterSheet
// inside a BottomSheet instead (see ContractorsListHeader). Unlike the sheet,
// this column never remounts, so its pending selection can't rely on a
// once-per-open lazy initializer; it's re-derived during render whenever the
// store's appliedFilters changes — the only thing that writes to the store
// while this is mounted without going through this component's own
// Apply/Clear handlers is ContractorsQueryProvider's one-time survey-prefill
// effect.
export default function FiltersSidebar() {
  const track = useInhomzTrack();
  const { appliedFilters, setAppliedFilters, clearFilters: clearStoreFilters } = useContractorFiltersStore();

  const toPending = (filters) => ({
    selected: { ...EMPTY_SELECTED, ...(filters?.selected || {}) },
  });

  const [pendingFilters, setPendingFilters] = useState(() => toPending(appliedFilters));

  // Adjust-state-during-render (not an effect) so an externally-driven store
  // change — e.g. ContractorsQueryProvider's one-time survey-prefill — shows
  // up here immediately, without this persistent (never-remounting) column
  // causing an extra render pass via useEffect.
  const [syncedAppliedFilters, setSyncedAppliedFilters] = useState(appliedFilters);
  if (appliedFilters !== syncedAppliedFilters) {
    setSyncedAppliedFilters(appliedFilters);
    setPendingFilters(toPending(appliedFilters));
  }

  // Clear/Apply read different signals, not the same "is anything selected"
  // flag: Clear must stay enabled as long as there's something to revert
  // (pending OR already-applied), even if the shopper has unchecked every
  // chip without hitting Apply yet — otherwise unchecking everything locks
  // out the one action that would actually reset the still-applied store
  // filters. Apply only cares whether the pending selection differs from
  // what's already applied, so hitting it twice in a row (or with nothing
  // changed) is a no-op and stays disabled.
  const appliedSelected = toPending(appliedFilters).selected;
  const hasAppliedSelection = Object.values(appliedSelected).some(
    (values) => values?.length > 0
  );
  const hasPendingSelection = Object.values(pendingFilters.selected).some(
    (values) => values?.length > 0
  );
  const hasPendingChanges = Object.keys(pendingFilters.selected).some((category) => {
    const pendingValues = pendingFilters.selected[category] || [];
    const appliedValues = appliedSelected[category] || [];
    if (pendingValues.length !== appliedValues.length) return true;
    const appliedSet = new Set(appliedValues);
    return pendingValues.some((value) => !appliedSet.has(value));
  });

  const canClear = hasPendingSelection || hasAppliedSelection;
  const canApply = hasPendingChanges;

  const toggleFilter = (category, value) => {
    track(inhomzEvents.contractorFilters.OPTION_TOGGLED, {
      source: "sidebar",
      category,
      event_value: value,
      selected: !pendingFilters.selected[category].includes(value),
    });
    setPendingFilters((prev) => {
      const isCurrentlySelected = prev.selected[category].includes(value);
      const isSelecting = !isCurrentlySelected;

      return {
        ...prev,
        selected: {
          ...prev.selected,
          [category]: isSelecting
            ? [...prev.selected[category], value]
            : prev.selected[category].filter((v) => v !== value),
        },
      };
    });
  };

  const handleClearFilters = () => {
    track(inhomzEvents.contractorFilters.CLEAR_CLICKED, { source: "sidebar" });
    setPendingFilters({ selected: EMPTY_SELECTED });
    clearStoreFilters();
  };

  const handleApplyFilters = () => {
    track(inhomzEvents.contractorFilters.APPLY_CLICKED, {
      source: "sidebar",
      ...buildFilterEventProps(pendingFilters.selected),
    });
    setAppliedFilters(pendingFilters);
  };

  const filterSections = Object.entries(FILTER_CONFIG).map(([category, config]) => ({
    title: config.title,
    options: config.options,
    category,
    selectedValues: pendingFilters.selected[category] || [],
  }));

  return (
    <aside className="hidden sticky top-16 w-[272px] flex-shrink-0 lg:block">
      <div className="flex max-h-[calc(100vh-104px)] flex-col rounded-[--radius28] border border-[--colorDisabledOnColorDisabled] bg-[--colorSurfaceSurface]">
        <div className="flex items-center gap-2 p-4 border-b border-[--colorDisabledOnColorDisabled]">
          <FilterListIcon sx={{ color: "var(--colorSecondarySecondary)", fontSize: "24px" }} />
          <span className="m3-title-small text-[--textPrimaryPrimary]">Filters</span>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar min-h-0">
          <div className="px-4 pb-6">
            {filterSections.map((section) => (
              <FilterSection key={section.category} {...section} toggleFilter={toggleFilter} />
            ))}
          </div>
        </div>

        <div className="flex gap-2 p-4 border-t border-[--colorDisabledOnColorDisabled]">
          <button
            onClick={handleClearFilters}
            disabled={!canClear}
            className={`h-10 flex-1 rounded-full m3-label-large transition-colors ${
              canClear
                ? "text-[--textLinkOnSurfaceLink]"
                : "text-[--textDisabledDisabled] cursor-not-allowed"
            }`}
          >
            Clear filters
          </button>
          <button
            onClick={handleApplyFilters}
            disabled={!canApply}
            className={`h-10 flex-1 rounded-full m3-label-large transition-colors ${
              canApply
                ? "bg-[--colorPrimaryPrimary] text-[--textPrimaryOnColorPrimary]"
                : "bg-[--colorDisabledDisabled] text-[--textDisabledOnColorDisabled] cursor-not-allowed"
            }`}
          >
            Apply filters
          </button>
        </div>
      </div>
    </aside>
  );
}
